/**
 * Atribución de Meta: llevar el clic del anuncio hasta la compra.
 *
 * El problema que resuelve es de tiempos. Alguien llega de un anuncio con
 * `?fbclid=...`, se registra, **confirma su correo horas después** y paga a los
 * días. Para entonces no queda nada de la URL original, así que el identificador
 * hay que guardarlo apenas se ve y persistirlo en el servidor en cuanto haya
 * sesión.
 *
 * Se usa `localStorage` y no `sessionStorage` a propósito: el registro no crea
 * sesión (hay que confirmar el correo), así que el usuario vuelve desde el enlace
 * del correo en otra pestaña. `sessionStorage` no sobrevive a eso.
 */

const FBCLID_KEY = "licitigo.fbclid";

/** Guarda el `fbclid` de la URL la primera vez que aparece. La primera gana. */
export function captureAdClickFromUrl(): void {
  const fbclid = new URLSearchParams(window.location.search).get("fbclid");
  if (!fbclid) return;

  try {
    if (localStorage.getItem(FBCLID_KEY)) return;
    localStorage.setItem(
      FBCLID_KEY,
      JSON.stringify({ fbclid, at: Date.now() }),
    );
  } catch {
    // Modo privado o almacenamiento bloqueado: se pierde la atribución, no la app.
  }
}

/**
 * Los dos identificadores que Meta necesita, leídos de las cookies que deja el
 * píxel. `_fbc` cae al `fbclid` guardado solo si el píxel no llegó a correr
 * (típicamente por un bloqueador).
 */
export function readMetaIdentifiers(): { fbp: string | null; fbc: string | null } {
  return {
    fbp: readCookie("_fbp"),
    fbc: readCookie("_fbc") ?? rebuildFbc(),
  };
}

function readCookie(name: string): string | null {
  const match = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${name}=([^;]*)`),
  );
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

/** Formato que exige Meta: `fb.<subdominio>.<cuándo se vio>.<fbclid>`. */
function rebuildFbc(): string | null {
  try {
    const raw = localStorage.getItem(FBCLID_KEY);
    if (!raw) return null;
    const { fbclid, at } = JSON.parse(raw) as { fbclid: string; at: number };
    return fbclid ? `fb.1.${at}.${fbclid}` : null;
  } catch {
    return null;
  }
}
